/* Copyright CloudLinux Zug GmbH 2010-2017 All Rights Reserved
 *
 * Licensed under CLOUD LINUX LICENSE AGREEMENT
 * http://cloudlinux.com/docs/LICENSE.TXT
 */

import { NgModule, ApplicationRef, ErrorHandler, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import { RouterModule, PreloadAllModules } from '@angular/router';
// import { removeNgStyles, createNewHosts, createInputTransfer } from '@angularclass/hmr';
import * as Raven from 'raven-js';
/*
 * Platform and Environment providers/directives/pipes
 */
import { ENV_PROVIDERS } from './environment';
import { ROUTES } from './app.routes';
// App is our top level component
import { App } from '../app.component';
import { AppState } from '../app.service';
import { CurrentComponent } from '../current.component';
import { LicenseComponent } from '../license.component';
import { OptionsComponent } from '../options.component';
import { PackagesComponent } from '../packages.component';
import { SelectorComponent } from '../selector.component';
import { StatsComponent } from '../stats.component';
import { UsersComponent } from '../users.component';
import { HistoricalComponent } from '../historical.component';
import { Popover } from '../utils/popover';
import { PackagesGrid } from '../utils/packages_grid';
import { UsersGrid } from '../utils/users_grid';
import { ModalStats } from '../utils/modal_stats';
import { NotificationsService } from '../services/notifiaction_service';
import { RavenTransport } from '../utils/raven_transport';
import { MockService } from '../services/mock';
import { ModalLimits } from '../utils/modal_limits';
import { ModalLimitsUser } from '../utils/modal_limits_user';
import { ManageViewMode } from '../utils/ui-elements/ui-manage-view-mode/ui-manage-view-mode';
import { UnavailableComponent } from '../unavailable.component';
import { UnavailableService } from '../services/unavailable-service';
import { TranslateLocaleHttpLoader } from '../utils/TranslateLocaleHttpLoader';
import { NodeJSIndexComponent } from '../nodejs/index.component';
import { NodeJSVersionComponent } from '../nodejs/version/version.component';
import { NodeJSApplicationComponent } from '../nodejs/application/application.component';
import { CagefsComponent } from '../cagefs-plugin/cagefs.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { XHR } from '../utils/xhr';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { CommonComponentModule } from '../../common.components';
import { CommonPipes } from '../utils/pipes';
import { ComponentModule } from '../utils/ui-elements';
import { TranslateLocaleMissingHandler } from '../utils/TranslateLocaleMissingHandler';
import { MissingTranslationHandler, TranslateLoader, TranslateModule,
    TranslateParser } from '@ngx-translate/core';
import { TranslateLocaleParser } from '../utils/TranslateLocaleParser';
import { TooltipModule } from 'ngx-bootstrap';
import { UiAccordionGroup } from '../utils/ui-elements/ui-accordion-group/ui-accordion-group';
import { WindowRef } from '../services/window.service';
import { CalendarModule } from '../utils/ui-elements/calendar/calendar.module';
import { IntervalService } from '../services/interval';
import { TemplateDirective } from '../utils/template.directive';
import { AngularMaterialModules }  from '../utils/angular_material_modules';
import { LoadingBarModule } from '@ngx-loading-bar/core';


const ENV = process.env.ENV;
// Application wide providers
const APP_PROVIDERS = [
    AppState,
    XHR,
];

class RavenErrorHandler implements ErrorHandler {

    handleError(err: any): void {
        Raven.captureException(err.originalError || err);
    }
}

export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateLocaleHttpLoader(http, 'i18n/');
}


/**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */
@NgModule({
    bootstrap: [ App ],
    declarations: [
        App,
        CurrentComponent,
        LicenseComponent,
        OptionsComponent,
        UiAccordionGroup,
        PackagesComponent,
        SelectorComponent,
        StatsComponent,
        UsersComponent,
        HistoricalComponent,
        Popover,
        PackagesGrid,
        UsersGrid,
        ModalStats,
        ModalLimits,
        ModalLimitsUser,
        ManageViewMode,
        NodeJSIndexComponent,
        NodeJSVersionComponent,
        NodeJSApplicationComponent,
        CagefsComponent,
        TemplateDirective,
    ],
    imports: [ // import Angular's modules
        BrowserModule,
        BrowserAnimationsModule,
        TooltipModule.forRoot(),
        FormsModule,
        RouterModule.forRoot(ROUTES, { useHash: true, preloadingStrategy: PreloadAllModules }),
        // ImunifyModule,
        BsDropdownModule.forRoot(),
        CalendarModule,
        HttpClientModule,
        HttpClientXsrfModule.withOptions({
          cookieName: 'csrftoken',
          headerName: 'X-CSRFToken',
        }),
        TranslateModule.forRoot({
           loader: {
               provide: TranslateLoader,
               useFactory: HttpLoaderFactory,
               deps: [HttpClient]
           },
           parser: {
               provide: TranslateParser,
               useClass: TranslateLocaleParser
           },
           missingTranslationHandler : {
               provide: MissingTranslationHandler,
               useClass: TranslateLocaleMissingHandler
           }
        }),
        CommonComponentModule,
        ComponentModule,
        CommonPipes,
        AngularMaterialModules,
        LoadingBarModule.forRoot(),
    ],
    entryComponents: [UnavailableComponent],
    providers: [ // expose our Services and Providers into Angular's dependency injection
        ENV_PROVIDERS,
        APP_PROVIDERS,
        NotificationsService, // no need to declare this 4 providers in each component
        RavenTransport,
        WindowRef,
        MockService,
        UnavailableService,
        IntervalService,
    ].concat(ENV === 'production' ? [{ provide: ErrorHandler, useClass: RavenErrorHandler }] :
      []), // Error Handler will block errors in development, don't use it there
    schemas: [ CUSTOM_ELEMENTS_SCHEMA ]
})
export class AppModule {
    constructor(public appRef: ApplicationRef,
                public appState: AppState,
                private xhrTransport: RavenTransport
    ) {
        this.initSentry();
    }

    private initSentry() {
        if(ENV === 'production') {
            Raven.config('https://7c2380b5311d46129e484f9805d5d1c6@sentry.io/136479', {
                tags: {
                    env: ENV
                },
                transport: (options) => this.xhrTransport.post(options),
                ignoreErrors: [
                    /.*please install or reinstall cagefs.*/,
                    /.*CageFS is not initialized. Use "\/usr\/sbin\/cagefsctl --init" to initialize CageFS.*/,
                    /.*%(what)s set error for package=%(package)s%(error)s.*/,
                    /.*%(what)s set error for uid=%(uid)s%(error)s.*/,
                    /.*Server is not running, Use \'service lvestats start\' command to start it.*/,
                    /.*alt-php packages not found.*/,
                    /.*no such script \/usr\/sbin\/cagefsctl.*/,
                    /.*License is not valid.*/,
                    /.*Session expired.*/,
                ]
            }).install();
        }
    }
}




// WEBPACK FOOTER //
// node_modules/angular2-template-loader!./node_modules/angular-router-loader/src?loader=system&genDir=compiled&aot=false!./src/app/core/app.module.ts